/**
 * TranslationSourceHint
 *
 * While a non-default language is being edited in the Form Builder, this shows
 * the primary/source value beneath a translation input as dimmed helper text —
 * like LinkedIn's inline "translate this" affordance — so the author can see
 * what they're translating. The overlaid form the editor renders has these
 * source strings blanked, so the value is supplied by the caller from the
 * primary form (see `utils/surveyTranslations#indexFormById`).
 *
 * Renders nothing when there is no source text (never translated, or editing
 * the primary itself), so call sites can mount it unconditionally.
 */

import React from 'react';
import classNames from 'classnames';
import helpers from 'utils/helpers';

/**
 * @param {Object} props
 * @param {string} [props.value]      the primary/source string for this field (may contain HTML)
 * @param {string} [props.className]  extra classes (e.g. `mt-1` for spacing at a given call site)
 * @returns {JSX.Element|null} the hint, or `null` when there is no source text to show
 */
export function TranslationSourceHint({ value, className }) {
    // Source strings can be rich text; show plain text so the hint stays a
    // single quiet line and never injects markup.
    const text = value ? helpers.removeHtml(value).trim() : '';
    if (!text) return null;

    return <p className={classNames('text-secondary fs-12 m-0 whitespace-pre-wrap', className)}>{text}</p>;
}

export default TranslationSourceHint;
